import 'swiper/scss';
import { FreeMode } from 'swiper';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Entry } from 'contentful';

import { client } from '../../helpers/contentful';
import { Spinner, ToolCard, SkeletonPage, SeoIntro } from '../../components';
import { IBrushFields } from '../../@types/generated/contentful';

const Stickers = () => {
  const [brushes, setBrushes] = useState<Entry<IBrushFields>[]>([]);
  const [newBrushes, setNewBrushes] = useState<Entry<IBrushFields>[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const getNewBrushes = async () => {
      const response = await client
        .getEntries<IBrushFields>({
          'fields.categoryName': 'Stickers & Textures',
          content_type: 'brush',
          'fields.featuredText[exists]': true,
        })
        .then((r) => r.items);

      return response;
    };

    getNewBrushes().then((r) => setNewBrushes(r));
  }, []);

  const getBrushes = async () => {
    const response = await client
      .getEntries<IBrushFields>({
        skip: brushes.length,
        limit: 12,
        'fields.categoryName': 'Stickers & Textures',
        content_type: 'brush',
      })
      .then((r) => r);

    if (total === 0) {
      setTotal(response.total);
    }

    setBrushes((state) => [...state, ...response.items]);
    return response.items;
  };

  useEffect(() => {
    getBrushes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='home'>
      {brushes.length > 0 && newBrushes ? (
        <>
          <SeoIntro
            eyebrow='Stickers and textures'
            title='Procreate stickers, texture assets, stamps, and overlays'
            description='Add Procreate stickers, texture overlays, stamps, patterns, and ready-made assets to speed up illustrations, journals, tattoos, and design work.'
          />
          {newBrushes.length > 0 && (
            <div className='border-t-2'>
              <div className='overflow-hidden px-6 py-8 xs:px-4 xs:py-6'>
                <div className='max-w-[1240px] mx-auto w-full'>
                  <div className='mx-auto pb-4'>
                    <h2 className='text-lg leading-xl font-bold'>For you</h2>
                  </div>
                  <Swiper
                    modules={[FreeMode]}
                    slidesPerView='auto'
                    spaceBetween={12}
                    centerInsufficientSlides
                    breakpoints={{ 480: { spaceBetween: 20 } }}
                    className='overflow-visible'
                  >
                    {newBrushes.map(({ fields, sys }) => (
                      <SwiperSlide className='w-[220px] sm:w-[180px] xs:w-[170px]' key={sys.id}>
                        <ToolCard size='small' set={fields} isBrushset category='Brushes' isFeatured />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          )}
          <div className='border-t-2 w-full'>
            <div className='mx-auto max-w-[1240px] w-full p-6 xs:p-4'>
              <h2 className='text-lg leading-xl font-bold mb-4'>All stickers & textures</h2>
              <InfiniteScroll
                dataLength={brushes.length}
                next={getBrushes}
                // eslint-disable-next-line react/jsx-boolean-value
                hasMore={brushes.length < total}
                scrollableTarget='app'
                style={{ overflow: 'visible' }}
                loader={
                  <div className='mt-4 flex justify-center'>
                    <Spinner theme='light' />
                  </div>
                }
              >
                <ul className='grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-5 xs:grid-cols-2 xs:gap-3 xxs:grid-cols-1'>
                  {brushes.map(({ fields, sys }) => (
                    <li key={sys.id}>
                      <ToolCard size='large' set={fields} category='Stickers & Textures' />
                    </li>
                  ))}
                </ul>
              </InfiniteScroll>
            </div>
          </div>
        </>
      ) : (
        <SkeletonPage title='stickers & textures' />
      )}
    </div>
  );
};

export { Stickers };
